import { useState, useMemo, useRef } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Plus,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PageHeaderActions } from '@/components/layout/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useTasks,
  useCompletedTasks,
  useCompleteTask,
  useCreateTask,
  useUpdateTask,
  type Task,
  type CreateTaskInput,
} from '@/hooks/use-tasks'
import { useAccounts } from '@/hooks/use-accounts'
import { useContacts } from '@/hooks/use-contacts'
import { cn } from '@/lib/utils'

const TASK_TYPE_KEYS = [
  'follow_up',
  'renewal_alert',
  'bom_followup',
  'call',
  'email',
  'revisit',
  'other',
] as const

const searchSchema = z.object({
  status: z.enum(['open', 'in_progress', 'completed']).optional(),
  task_type: z.enum(TASK_TYPE_KEYS).optional(),
  account_id: z.string().uuid().optional(),
  due_from: z.string().optional(),
  due_to: z.string().optional(),
})

export const Route = createFileRoute('/_authed/tasks')({
  validateSearch: searchSchema,
  component: Tasks,
})

const TASK_TYPE_LABELS: Record<string, string> = {
  follow_up: 'Follow Up',
  renewal_alert: 'Renewal',
  bom_followup: 'BOM Follow-up',
  call: 'Call',
  email: 'Email',
  revisit: 'Revisit',
  other: 'Other',
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  account: 'Account',
  contact: 'Contact',
  pipeline: 'Deal',
  ams_contract: 'AMS Contract',
}

const TASK_TYPES = Object.entries(TASK_TYPE_LABELS) as [string, string][]

type EntityLink = {
  entity_type: 'account' | 'contact'
  entity_id: string
  entity_name: string
}

function formatTaskDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr + 'T00:00:00'))
}

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function Tasks() {
  const today = todayStr()
  const search = useSearch({ from: '/_authed/tasks' })
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: tasks = [], isLoading } = useTasks()
  const { data: completedTasks = [] } = useCompletedTasks()
  const { mutate: completeTask } = useCompleteTask()
  const { mutate: createTask } = useCreateTask()
  const { data: accounts = [] } = useAccounts()
  const { data: contacts = [] } = useContacts()

  const [completedOpen, setCompletedOpen] = useState(false)

  // Quick-create form state
  const [createTitle, setCreateTitle] = useState('')
  const [createDueDate, setCreateDueDate] = useState('')
  const [createTaskType, setCreateTaskType] =
    useState<CreateTaskInput['task_type']>('follow_up')
  const [createEntity, setCreateEntity] = useState<EntityLink | null>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)

  const entityNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const acc of accounts) map[acc.id] = acc.name
    for (const con of contacts) {
      map[con.id] = `${con.first_name} ${con.last_name}`
    }
    return map
  }, [accounts, contacts])

  const hasFilters = !!(
    search.status ||
    search.task_type ||
    search.account_id ||
    search.due_from ||
    search.due_to
  )

  function setFilter(next: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ to: '/tasks', search: (prev) => ({ ...prev, ...next }) })
  }

  function clearFilters() {
    void navigate({ to: '/tasks', search: () => ({}) })
  }

  // Apply in-memory filters to the open/in_progress task list
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (search.status) {
        // 'completed' tasks are never in useTasks() result — filtering to completed means empty open list
        if (search.status === 'completed') return false
        if (task.status !== search.status) return false
      }
      if (search.task_type && task.task_type !== search.task_type) return false
      if (search.account_id) {
        if (
          task.entity_type !== 'account' ||
          task.entity_id !== search.account_id
        )
          return false
      }
      if (search.due_from && task.due_date && task.due_date < search.due_from)
        return false
      if (search.due_to && task.due_date && task.due_date > search.due_to)
        return false
      return true
    })
  }, [tasks, search])

  const { overdue, upcoming } = useMemo(() => {
    const overdue: Task[] = []
    const upcoming: Task[] = []
    for (const task of filteredTasks) {
      if (task.due_date && task.due_date < today) {
        overdue.push(task)
      } else {
        upcoming.push(task)
      }
    }
    return { overdue, upcoming }
  }, [filteredTasks, today])

  // Auto-expand completed section when the status filter selects 'completed'
  const effectiveCompletedOpen = completedOpen || search.status === 'completed'

  const filterAccountName = search.account_id
    ? (accounts.find((a) => a.id === search.account_id)?.name ?? null)
    : null

  function handleCreate() {
    if (!createTitle.trim()) return
    createTask(
      {
        title: createTitle.trim(),
        due_date: createDueDate || null,
        task_type: createTaskType,
        entity_type: createEntity?.entity_type ?? null,
        entity_id: createEntity?.entity_id ?? null,
      },
      {
        onSuccess: () => {
          setCreateTitle('')
          setCreateDueDate('')
          setCreateTaskType('follow_up')
          setCreateEntity(null)
          titleInputRef.current?.focus()
        },
      }
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Slice 1B-1: the count joins the ONE shared header via the portal
          slot — the title comes from the nav registry there. */}
      <PageHeaderActions>
        {!isLoading && (
          <Badge variant="secondary">{filteredTasks.length}</Badge>
        )}
      </PageHeaderActions>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 border-b px-6 py-3">
        <Select
          value={search.status ?? '__all__'}
          onValueChange={(v) =>
            setFilter({
              status:
                v === '__all__'
                  ? undefined
                  : (v as 'open' | 'in_progress' | 'completed'),
            })
          }
        >
          <SelectTrigger
            aria-label="Task status filter"
            className="h-8 w-[130px]"
          >
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={search.task_type ?? '__all__'}
          onValueChange={(v) =>
            setFilter({
              task_type:
                v === '__all__'
                  ? undefined
                  : (v as (typeof TASK_TYPE_KEYS)[number]),
            })
          }
        >
          <SelectTrigger
            aria-label="Task type filter"
            className="h-8 w-[130px]"
          >
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {TASK_TYPES.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <AccountFilterCombobox
          accounts={accounts}
          value={search.account_id ?? null}
          displayName={filterAccountName}
          onChange={(id) => setFilter({ account_id: id ?? undefined })}
        />

        <div className="flex items-center gap-1">
          <input
            type="date"
            value={search.due_from ?? ''}
            onChange={(e) =>
              setFilter({ due_from: e.target.value || undefined })
            }
            aria-label="Due from"
            className="h-8 w-[130px] rounded-md border border-[color:var(--interactive-border)] bg-background px-2 text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-1"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <input
            type="date"
            value={search.due_to ?? ''}
            onChange={(e) => setFilter({ due_to: e.target.value || undefined })}
            aria-label="Due to"
            className="h-8 w-[130px] rounded-md border border-[color:var(--interactive-border)] bg-background px-2 text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-1"
          />
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={clearFilters}
          >
            <X className="mr-1 h-3 w-3" />
            Clear all
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <div className="space-y-4 px-6 py-4">
          {isLoading ? (
            <TasksSkeleton />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-9" />
                    <TableHead>Task</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Linked to</TableHead>
                    <TableHead className="w-[140px] tabular-nums">
                      Due
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="px-4 align-top">
                        {hasFilters ? (
                          <EmptyState
                            title="No tasks match these filters"
                            description="Try clearing or adjusting your filters to see more."
                          />
                        ) : (
                          <EmptyState
                            title="No open tasks"
                            description="Follow-ups assigned to you — created from an account, contact, or deal — show up here. You’re all caught up."
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {overdue.length > 0 && (
                    <>
                      <TableRow className="bg-destructive/5 hover:bg-destructive/5">
                        <TableCell
                          colSpan={5}
                          className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-destructive"
                        >
                          <span className="flex items-center gap-1.5">
                            <AlertCircle className="h-3 w-3" />
                            Overdue · {overdue.length}
                          </span>
                        </TableCell>
                      </TableRow>
                      {overdue.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          isOverdue
                          entityName={
                            task.entity_id
                              ? entityNameMap[task.entity_id]
                              : undefined
                          }
                          onComplete={completeTask}
                        />
                      ))}
                    </>
                  )}

                  {upcoming.length > 0 && overdue.length > 0 && (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell
                        colSpan={5}
                        className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        Upcoming · {upcoming.length}
                      </TableCell>
                    </TableRow>
                  )}

                  {upcoming.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      isToday={task.due_date === today}
                      entityName={
                        task.entity_id
                          ? entityNameMap[task.entity_id]
                          : undefined
                      }
                      onComplete={completeTask}
                    />
                  ))}

                  {/* Quick-create row */}
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="w-9 text-center">
                      <Plus className="mx-auto h-4 w-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <Input
                        ref={titleInputRef}
                        value={createTitle}
                        onChange={(e) => setCreateTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreate()
                          if (e.key === 'Escape') setCreateTitle('')
                        }}
                        placeholder="New task…"
                        className="h-7 border-transparent bg-transparent px-0 text-sm shadow-none focus-visible:border-[color:var(--interactive-border)] focus-visible:bg-background focus-visible:px-3 focus-visible:shadow-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={createTaskType}
                        onValueChange={(v) =>
                          setCreateTaskType(v as CreateTaskInput['task_type'])
                        }
                      >
                        <SelectTrigger
                          aria-label="New task type"
                          className="h-7 w-[110px] text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_TYPES.map(([value, label]) => (
                            <SelectItem
                              key={value}
                              value={value}
                              className="text-xs"
                            >
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <EntityLinkCombobox
                        accounts={accounts}
                        contacts={contacts}
                        value={createEntity}
                        onChange={setCreateEntity}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <input
                          type="date"
                          value={createDueDate}
                          onChange={(e) => setCreateDueDate(e.target.value)}
                          className="h-7 w-[130px] rounded-md border border-[color:var(--interactive-border)] bg-background px-2 text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-1"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          disabled={!createTitle.trim()}
                          onClick={handleCreate}
                        >
                          Add
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {/* Completed section — always rendered, collapsed by default */}
          <div className="rounded-md border">
            <button
              type="button"
              onClick={() => setCompletedOpen((o) => !o)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/30"
            >
              {effectiveCompletedOpen ? (
                <ChevronDown className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0" />
              )}
              Completed
              <span className="ml-1 text-xs font-normal">
                ({completedTasks.length})
              </span>
            </button>

            {effectiveCompletedOpen && completedTasks.length > 0 && (
              <div className="border-t">
                <Table>
                  <TableBody>
                    {completedTasks.map((task) => (
                      <CompletedRow
                        key={task.id}
                        task={task}
                        entityName={
                          task.entity_id
                            ? entityNameMap[task.entity_id]
                            : undefined
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {effectiveCompletedOpen && completedTasks.length === 0 && (
              <p className="border-t px-4 py-3 text-sm text-muted-foreground">
                No completed tasks.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function AccountFilterCombobox({
  accounts,
  value,
  displayName,
  onChange,
}: {
  accounts: Array<{ id: string; name: string }>
  value: string | null
  displayName: string | null
  onChange: (id: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const q = search.toLowerCase()
  const filtered = useMemo(() => {
    const list = q
      ? accounts.filter((a) => a.name.toLowerCase().includes(q))
      : accounts
    return list.slice(0, 10)
  }, [accounts, q])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-label="Account filter"
          aria-expanded={open}
          className={cn(
            'h-8 w-[160px] justify-between text-xs font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          <span className="truncate">{displayName ?? 'All accounts'}</span>
          {value ? (
            <X
              className="h-3 w-3 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onChange(null)
              }}
            />
          ) : (
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search accounts…"
          />
          <CommandList>
            {filtered.length === 0 && (
              <CommandEmpty>No accounts found.</CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={a.id}
                    onSelect={() => {
                      onChange(a.id)
                      setSearch('')
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === a.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {a.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function EntityLinkCombobox({
  accounts,
  contacts,
  value,
  onChange,
}: {
  accounts: Array<{ id: string; name: string }>
  contacts: Array<{ id: string; first_name: string; last_name: string }>
  value: EntityLink | null
  onChange: (v: EntityLink | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const q = search.toLowerCase()

  const filteredAccounts = useMemo(() => {
    const list = q
      ? accounts.filter((a) => a.name.toLowerCase().includes(q))
      : accounts
    return list.slice(0, 8)
  }, [accounts, q])

  const filteredContacts = useMemo(() => {
    const list = q
      ? contacts.filter((c) =>
          `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
        )
      : contacts
    return list.slice(0, 8)
  }, [contacts, q])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-label="Link task to account or contact"
          aria-expanded={open}
          className={cn(
            'h-7 w-full justify-between text-xs font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          <span className="truncate">
            {value ? value.entity_name : 'Link to…'}
          </span>
          {value ? (
            <X
              className="h-3 w-3 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onChange(null)
              }}
            />
          ) : (
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search accounts or contacts…"
          />
          <CommandList>
            {filteredAccounts.length === 0 && filteredContacts.length === 0 && (
              <CommandEmpty>No results.</CommandEmpty>
            )}
            {filteredAccounts.length > 0 && (
              <CommandGroup heading="Accounts">
                {filteredAccounts.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`account:${a.id}`}
                    onSelect={() => {
                      onChange({
                        entity_type: 'account',
                        entity_id: a.id,
                        entity_name: a.name,
                      })
                      setSearch('')
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value?.entity_id === a.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {a.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filteredAccounts.length > 0 && filteredContacts.length > 0 && (
              <CommandSeparator />
            )}
            {filteredContacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {filteredContacts.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`contact:${c.id}`}
                    onSelect={() => {
                      onChange({
                        entity_type: 'contact',
                        entity_id: c.id,
                        entity_name: `${c.first_name} ${c.last_name}`,
                      })
                      setSearch('')
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value?.entity_id === c.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {c.first_name} {c.last_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function TaskRow({
  task,
  isOverdue = false,
  isToday = false,
  entityName,
  onComplete,
}: {
  task: Task
  isOverdue?: boolean
  isToday?: boolean
  entityName?: string
  onComplete?: (id: string) => void
}) {
  const { mutate: updateTask } = useUpdateTask()
  const [title, setTitle] = useState(task.title)
  const [dueDate, setDueDate] = useState(task.due_date ?? '')
  const [taskType, setTaskType] = useState<Task['task_type']>(task.task_type)

  // Sync local state when the server confirms or rolls back an update
  const [prevTask, setPrevTask] = useState(task)
  if (
    task.title !== prevTask.title ||
    task.due_date !== prevTask.due_date ||
    task.task_type !== prevTask.task_type
  ) {
    setPrevTask(task)
    setTitle(task.title)
    setDueDate(task.due_date ?? '')
    setTaskType(task.task_type)
  }

  function commitTitle() {
    const trimmed = title.trim()
    if (!trimmed) {
      setTitle(task.title)
      return
    }
    if (trimmed !== task.title) updateTask({ id: task.id, title: trimmed })
  }

  function commitDueDate() {
    const next = dueDate || null
    const prev = task.due_date ?? null
    if (next !== prev) updateTask({ id: task.id, due_date: next })
  }

  function handleTypeChange(v: string) {
    const next = v as Task['task_type']
    setTaskType(next)
    if (next !== task.task_type) updateTask({ id: task.id, task_type: next })
  }

  return (
    <TableRow className={cn(isToday && 'bg-amber-50/60 dark:bg-amber-900/10')}>
      <TableCell className="w-9">
        <Checkbox
          onCheckedChange={() => onComplete?.(task.id)}
          aria-label={`Complete ${task.title}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') {
              setTitle(task.title)
              e.currentTarget.blur()
            }
          }}
          className="h-7 border-transparent bg-transparent px-0 text-sm font-medium shadow-none focus-visible:border-[color:var(--interactive-border)] focus-visible:bg-background focus-visible:px-3 focus-visible:shadow-sm"
        />
      </TableCell>
      <TableCell>
        <Select value={taskType} onValueChange={handleTypeChange}>
          <SelectTrigger
            aria-label={`Task type for ${task.title}`}
            className="h-7 w-[110px] border-transparent bg-transparent text-xs shadow-none data-[state=open]:border-[color:var(--interactive-border)] data-[state=open]:bg-background"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TASK_TYPES.map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        {entityName ? (
          <span className="text-xs text-muted-foreground">{entityName}</span>
        ) : task.entity_type ? (
          <span className="text-xs text-muted-foreground">
            {ENTITY_TYPE_LABELS[task.entity_type] ?? task.entity_type}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          onBlur={commitDueDate}
          aria-label="Due date"
          className={cn(
            'h-7 w-[130px] rounded-md border border-transparent bg-transparent px-2 text-xs tabular-nums focus:border-[color:var(--interactive-border)] focus:bg-background focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-1',
            isOverdue && 'font-medium text-destructive',
            isToday && 'font-medium text-amber-600 dark:text-amber-400'
          )}
        />
      </TableCell>
    </TableRow>
  )
}

function CompletedRow({
  task,
  entityName,
}: {
  task: Task
  entityName?: string
}) {
  return (
    <TableRow className="opacity-60">
      <TableCell className="w-9">
        <Checkbox
          checked
          disabled
          aria-label={`Completed ${task.title}`}
        />
      </TableCell>
      <TableCell className="font-medium text-muted-foreground line-through">
        {task.title}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs font-normal">
          {TASK_TYPE_LABELS[task.task_type] ?? task.task_type}
        </Badge>
      </TableCell>
      <TableCell>
        {entityName ? (
          <span className="text-xs text-muted-foreground">{entityName}</span>
        ) : task.entity_type ? (
          <span className="text-xs text-muted-foreground">
            {ENTITY_TYPE_LABELS[task.entity_type] ?? task.entity_type}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm tabular-nums text-muted-foreground">
        {task.due_date ? formatTaskDate(task.due_date) : <span>—</span>}
      </TableCell>
    </TableRow>
  )
}

function TasksSkeleton() {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-9" />
            <TableHead>Task</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Linked to</TableHead>
            <TableHead className="w-[140px]">Due</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="h-4 w-4 animate-pulse rounded bg-muted" />
              </TableCell>
              <TableCell>
                <div className="h-4 w-48 animate-pulse rounded bg-muted" />
              </TableCell>
              <TableCell>
                <div className="h-4 w-20 animate-pulse rounded bg-muted" />
              </TableCell>
              <TableCell>
                <div className="h-4 w-16 animate-pulse rounded bg-muted" />
              </TableCell>
              <TableCell>
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
